import React, { useState } from 'react';
import { Globe2, MapPin, Building2, Layers, Sparkles, ArrowRight, Share2, ChevronRight, FileText } from 'lucide-react';
import { CountUp } from './hooks';
import { WrappedStats, COUNTRY_GEO } from './types';
import WorldMap from './WorldMap';

interface SlideProps {
  stats: WrappedStats;
  active: boolean;
}

/* ----------------------------------------------------------------- 1 · Intro */
const IntroSlide: React.FC<SlideProps> = () => (
  <div className="wrapped-slide-inner">
    <span className="wrapped-kicker reveal">Premios NovaGob · Excelencia 2026</span>
    <h1 className="wrapped-title reveal">
      Esto es lo que <span className="gold">construimos juntos</span>
    </h1>
    <p className="wrapped-sub reveal">
      Un recorrido por las candidaturas de esta edición: de dónde vienen, quién las
      impulsa y cuántas ideas mueven hoy lo público.
    </p>
    <span className="wrapped-hint reveal">
      Toca para avanzar <ChevronRight size={15} />
    </span>
  </div>
);

/* --------------------------------------------------------------- 2 · Total */
const TotalSlide: React.FC<SlideProps> = ({ stats, active }) => (
  <div className="wrapped-slide-inner">
    <span className="wrapped-kicker reveal">
      <Sparkles size={13} style={{ verticalAlign: '-2px', marginRight: 6 }} />
      Este año
    </span>
    <p className="wrapped-sub reveal" style={{ marginBottom: -6 }}>Habéis presentado</p>
    <div className="wrapped-bignum reveal">
      <CountUp to={stats.totals.received} active={active} duration={1900} />
    </div>
    <h2 className="wrapped-title reveal" style={{ fontSize: 'clamp(24px,4.4vw,40px)' }}>
      candidaturas
    </h2>
    <p className="wrapped-sub reveal">desde toda la comunidad iberoamericana del sector público.</p>
  </div>
);

/* ------------------------------------------------------------- 3 · Mapa */
const MapSlide: React.FC<SlideProps> = ({ stats, active }) => {
  // El mapa solo puede pintar países con coordenadas conocidas; contamos
  // esos para que el titular cuadre con los nodos visibles.
  const known = stats.countries.filter((c) => COUNTRY_GEO[c.name]);
  return (
    <div className="wrapped-slide-inner">
      <span className="wrapped-kicker reveal">
        <Globe2 size={13} style={{ verticalAlign: '-2px', marginRight: 6 }} />
        Innovación sin fronteras
      </span>
      <h2 className="wrapped-title reveal" style={{ fontSize: 'clamp(24px,4.6vw,44px)' }}>
        <span className="gold">{known.length} países</span>, una vocación común
      </h2>
      <div className="reveal" style={{ width: '100%' }}>
        <WorldMap countries={stats.countries} active={active} />
      </div>
    </div>
  );
};

/* --------------------------------------------------------- 4 · Comunidades */
const RegionsSlide: React.FC<SlideProps> = ({ stats, active }) => {
  const top = stats.regions.slice(0, 8);
  const rest = stats.regions.length - top.length;
  const max = top.length ? top[0].count : 1;
  return (
    <div className="wrapped-slide-inner">
      <span className="wrapped-kicker reveal">
        <MapPin size={13} style={{ verticalAlign: '-2px', marginRight: 6 }} />
        España, comunidad a comunidad
      </span>
      <h2 className="wrapped-title reveal" style={{ fontSize: 'clamp(22px,4.2vw,40px)' }}>
        Madrid, Andalucía y Cataluña <span className="gold">a la cabeza</span>
      </h2>
      <div className="wrapped-bars reveal">
        {top.map((r) => (
          <div className="wrapped-bar-row" key={r.name}>
            <span className="wrapped-bar-label" title={r.name}>{r.name}</span>
            <span className="wrapped-bar-track">
              <span className="wrapped-bar-fill" style={{ width: active ? `${(r.count / max) * 100}%` : 0 }} />
            </span>
            <span className="wrapped-bar-val">
              <CountUp to={r.count} active={active} duration={1200} />
            </span>
          </div>
        ))}
      </div>
      {rest > 0 && (
        <p className="wrapped-sub reveal" style={{ fontSize: 14 }}>
          y {rest} comunidades autónomas más.
        </p>
      )}
    </div>
  );
};

/* --------------------------------------------------- 5 · Niveles de gobierno */
const GOV_COLORS = ['#ffd702', '#4fb0ff', '#35d6a4', '#b98bff', '#ff8ba7', '#9aa7b4', '#e0e0e0'];

const GovSlide: React.FC<SlideProps> = ({ stats, active }) => {
  const levels = stats.gov_levels;
  const R = 82;
  const C = 2 * Math.PI * R;
  let acc = 0;

  // Etiqueta compacta: quita el prefijo "Administración Pública".
  const shortName = (n: string) =>
    n.replace(/^Administraci[oó]n P[uú]blica\s*/i, '').replace(/^./, (m) => m.toUpperCase()) || n;

  return (
    <div className="wrapped-slide-inner">
      <span className="wrapped-kicker reveal">
        <Layers size={13} style={{ verticalAlign: '-2px', marginRight: 6 }} />
        ¿Desde dónde innova el sector público?
      </span>
      <h2 className="wrapped-title reveal" style={{ fontSize: 'clamp(22px,4.2vw,40px)' }}>
        4 de cada 10, desde lo <span className="gold">local</span>
      </h2>
      <div className="wrapped-donut-wrap reveal">
        <svg className="wrapped-donut" viewBox="0 0 230 230">
          <circle cx="115" cy="115" r={R} stroke="rgba(255,255,255,0.06)" />
          {levels.map((lv, i) => {
            const len = (lv.pct / 100) * C;
            const startAngle = -90 + (acc / 100) * 360;
            acc += lv.pct;
            return (
              <circle
                key={lv.name}
                className="seg"
                cx="115"
                cy="115"
                r={R}
                stroke={GOV_COLORS[i % GOV_COLORS.length]}
                strokeDasharray={`${len} ${C}`}
                strokeDashoffset={active ? 0 : len}
                transform={`rotate(${startAngle} 115 115)`}
                style={{ transitionDelay: `${i * 0.12}s` }}
              />
            );
          })}
          <text className="wrapped-donut-center" x="115" y="108" textAnchor="middle">
            <tspan className="__b" x="115" dy="0" style={{ fontSize: 30, fontWeight: 800, fill: '#fff' }}>
              {levels.length}
            </tspan>
          </text>
          <text className="wrapped-donut-center" x="115" y="132" textAnchor="middle">
            ámbitos
          </text>
        </svg>
        <div className="wrapped-legend">
          {levels.map((lv, i) => (
            <div className="wrapped-legend-row" key={lv.name}>
              <span className="wrapped-legend-dot" style={{ background: GOV_COLORS[i % GOV_COLORS.length] }} />
              <span className="pct"><CountUp to={Math.round(lv.pct)} active={active} duration={1200} />%</span>
              <span className="lbl">{shortName(lv.name)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ---------------------------------------------------------- 6 · Categorías */
const CategoriesSlide: React.FC<SlideProps> = ({ stats, active }) => {
  const cats = stats.categories;
  const max = cats.length ? cats[0].count : 1;
  return (
    <div className="wrapped-slide-inner">
      <span className="wrapped-kicker reveal">
        <Sparkles size={13} style={{ verticalAlign: '-2px', marginRight: 6 }} />
        {cats.length} categorías, {stats.totals.received} ideas
      </span>
      <h2 className="wrapped-title reveal" style={{ fontSize: 'clamp(22px,4.2vw,40px)' }}>
        ¿Dónde compiten <span className="gold">las candidaturas</span>?
      </h2>
      <div className="wrapped-catgrid reveal">
        {cats.map((c) => (
          <div className="wrapped-cat" key={c.name}>
            <div className="wrapped-cat-head">
              <span className={`wrapped-cat-dot type-${c.type}`} />
              <span className="wrapped-cat-name">{c.name}</span>
              <span className="wrapped-cat-val">
                <CountUp to={c.count} active={active} duration={1200} />
              </span>
            </div>
            <span className="wrapped-cat-track">
              <span
                className={`wrapped-cat-fill type-${c.type}`}
                style={{ width: active ? `${(c.count / max) * 100}%` : 0 }}
              />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------- 7 · Embudo */
const FunnelSlide: React.FC<SlideProps> = ({ stats, active }) => {
  const { received, approved, rejected } = stats.totals;
  const base = received || 1; // evita división por cero si aún no hay candidaturas
  const cols = [
    { n: received, label: 'Recibidas', dim: false, h: 100 },
    { n: approved, label: 'Aprobadas', dim: false, h: Math.max(18, (approved / base) * 100) },
    { n: rejected, label: 'Rechazadas', dim: true, h: Math.max(18, (rejected / base) * 100) },
  ];
  return (
    <div className="wrapped-slide-inner">
      <span className="wrapped-kicker reveal">
        <ArrowRight size={13} style={{ verticalAlign: '-2px', marginRight: 6 }} />
        Del envío al reconocimiento
      </span>
      <h2 className="wrapped-title reveal" style={{ fontSize: 'clamp(22px,4.2vw,40px)' }}>
        Cada candidatura, <span className="gold">revisada una a una</span>
      </h2>
      <div className="wrapped-funnel reveal" style={{ height: 260 }}>
        {cols.map((c) => (
          <div className="wrapped-funnel-col" key={c.label}>
            <span className="wrapped-funnel-num">
              <CountUp to={c.n} active={active} duration={1500} />
            </span>
            <div
              className={`wrapped-funnel-bar${c.dim ? ' dim' : ''}`}
              style={{ height: active ? `${c.h * 1.6}px` : 0 }}
            />
            <span className="wrapped-funnel-lbl">{c.label}</span>
          </div>
        ))}
      </div>
      <p className="wrapped-sub reveal" style={{ fontSize: 14 }}>
        Un jurado de expertas y expertos evalúa cada propuesta con los mismos criterios.
      </p>
    </div>
  );
};

/* ------------------------------------------------------- 8 · Instituciones */
const InstitutionsSlide: React.FC<SlideProps> = ({ stats, active }) => {
  const names = stats.institutions.names;
  // reparte en 3 filas
  const rows: string[][] = [[], [], []];
  names.forEach((n, i) => rows[i % 3].push(n));
  return (
    <div className="wrapped-slide-inner">
      <span className="wrapped-kicker reveal">
        <Building2 size={13} style={{ verticalAlign: '-2px', marginRight: 6 }} />
        Quiénes están
      </span>
      <div className="wrapped-bignum reveal" style={{ fontSize: 'clamp(64px,15vw,150px)' }}>
        <CountUp to={stats.institutions.count} active={active} duration={1800} />
      </div>
      <h2 className="wrapped-title reveal" style={{ fontSize: 'clamp(20px,3.6vw,34px)', marginTop: -6 }}>
        instituciones públicas participantes
      </h2>
      {names.length > 0 && (
        <div className="reveal wrapped-marquee-rows">
          {rows.filter((r) => r.length > 0).map((row, ri) => (
            <div className="wrapped-marquee-wrap" key={ri}>
              <div
                className={`wrapped-marquee${ri % 2 === 1 ? ' reverse' : ''}`}
                style={{ ['--marquee-dur' as any]: `${38 + ri * 8}s` }}
              >
                {[...row, ...row].map((name, i) => (
                  <span className="wrapped-inst" key={i}>{name}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ----------------------------------------------------------- 9 · Cierre */
const ClosingSlide: React.FC<SlideProps> = () => {
  const [copied, setCopied] = useState(false);
  const share = async () => {
    const url = window.location.origin + '/candidaturas';
    const data = { title: 'Premios NovaGob Excelencia 2026', text: 'Descubre las candidaturas de esta edición', url };
    try {
      if (navigator.share) {
        await navigator.share(data);
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      }
    } catch {
      /* el usuario canceló el diálogo de compartir */
    }
  };
  return (
    <div className="wrapped-slide-inner">
      <span className="wrapped-kicker reveal">Premios NovaGob · Excelencia 2026</span>
      <h1 className="wrapped-title reveal">
        Gracias por construir <span className="gold">lo público</span>
      </h1>
      <p className="wrapped-sub reveal">
        El jurado ya está deliberando. Los ganadores, muy pronto.
      </p>
      <div className="wrapped-actions reveal">
        <a className="wrapped-btn primary" href="/">
          Volver a la web <ArrowRight size={17} />
        </a>
        <button className="wrapped-btn ghost" onClick={share} type="button">
          <Share2 size={16} /> Compartir
        </button>
        <a className="wrapped-btn ghost" href="/informe-candidaturas">
          <FileText size={16} /> Ver informe completo
        </a>
      </div>
      <span className="wrapped-copied reveal">{copied ? '¡Enlace copiado!' : ''}</span>
    </div>
  );
};

export interface SlideDef {
  key: string;
  duration: number;
  title: string; // etiqueta corta para anunciar el slide a lectores de pantalla
  Comp: React.FC<SlideProps>;
}

export const SLIDES: SlideDef[] = [
  { key: 'intro', duration: 4200, title: 'Bienvenida', Comp: IntroSlide },
  { key: 'total', duration: 4600, title: 'Total de candidaturas', Comp: TotalSlide },
  { key: 'map', duration: 8500, title: 'Países participantes', Comp: MapSlide },
  { key: 'regions', duration: 6800, title: 'Comunidades autónomas', Comp: RegionsSlide },
  { key: 'gov', duration: 6800, title: 'Niveles de gobierno', Comp: GovSlide },
  { key: 'categories', duration: 7800, title: 'Categorías', Comp: CategoriesSlide },
  { key: 'funnel', duration: 6200, title: 'Proceso de valoración', Comp: FunnelSlide },
  { key: 'institutions', duration: 7200, title: 'Instituciones participantes', Comp: InstitutionsSlide },
  { key: 'closing', duration: 0, title: 'Cierre', Comp: ClosingSlide },
];
